/**
 * Editor CSS Styles
 * All styles are injected into Shadow DOM — no leakage to host page.
 */

export const EDITOR_CSS = `
/* Reset inside shadow DOM */
:host {
  all: initial;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  color: #1a1a2e;
  line-height: 1.5;
  box-sizing: border-box;
}

*, *::before, *::after {
  box-sizing: border-box;
}

/* ── FAB (replaces fixed toolbar) ── */
.lr-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  gap: 0;
  user-select: none;
}

.lr-fab-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #2d7f7f;
  color: #fff;
  border: none;
  font-weight: 700;
  font-size: 16px;
  cursor: grab;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  transition: box-shadow 0.15s;
  flex-shrink: 0;
  z-index: 1;
}

.lr-fab-btn:hover {
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
}

.lr-fab-btn:active {
  cursor: grabbing;
}

.lr-fab-panel {
  display: none;
  align-items: center;
  gap: 8px;
  background: #1a1a2e;
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  margin-right: 8px; /* gap between panel (left) and the FAB button (right) */
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  white-space: nowrap;
}

.lr-fab.expanded .lr-fab-panel {
  display: flex;
}

.lr-lang-dropdown {
  position: relative;
}

.lr-lang-btn {
  background: rgba(255,255,255,0.1);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
}

.lr-lang-btn:hover {
  background: rgba(255,255,255,0.2);
}

.lr-lang-menu {
  display: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 0;
  background: #1a1a2e;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  padding: 4px;
  min-width: 60px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
}

.lr-lang-menu.open {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lr-lang-item {
  background: none;
  color: #ccc;
  border: none;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  text-align: left;
}

.lr-lang-item:hover {
  background: rgba(255,255,255,0.1);
  color: #fff;
}

.lr-lang-item.active {
  background: #2d7f7f;
  color: #fff;
}

.lr-fab-progress {
  font-size: 13px;
  color: rgba(255,255,255,0.7);
}

.lr-fab-exit {
  background: none;
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.lr-fab-exit:hover {
  background: rgba(255,255,255,0.1);
}

.lr-fab-snapshot {
  background: #2d7f7f;
  border: 1px solid #2d7f7f;
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.lr-fab-snapshot:hover {
  background: #3a9999;
  border-color: #3a9999;
}

.lr-fab-snapshot:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lr-fab-browse {
  background: none;
  border: 1px solid rgba(255,255,255,0.3);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
}

.lr-fab-browse:hover {
  background: rgba(255,255,255,0.1);
}

.lr-fab-browse.active {
  background: #f5a623;
  border-color: #f5a623;
  color: #1a1a2e;
  font-weight: 600;
}

.lr-link-nav {
  background: none;
  border: 1px solid #2d7f7f;
  color: #2d7f7f;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}

.lr-link-nav:hover {
  background: #2d7f7f;
  color: #fff;
}

/* Workflow action buttons (Phase 3) */
.lr-workflow-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 12px 0 4px;
  padding: 8px 0 0;
  border-top: 1px solid #e5e7eb;
}

.lr-wf-btn {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.1s;
}

.lr-wf-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.lr-wf-primary {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}
.lr-wf-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.lr-wf-secondary {
  background: white;
  color: #374151;
  border-color: #d1d5db;
}
.lr-wf-secondary:hover:not(:disabled) {
  background: #f3f4f6;
}

.lr-wf-danger {
  background: white;
  color: #b91c1c;
  border-color: #fca5a5;
}
.lr-wf-danger:hover:not(:disabled) {
  background: #fef2f2;
}

.lr-wf-note {
  font-size: 11px;
  color: #6b7280;
  font-style: italic;
  padding: 4px 0;
}

/* Workflow context strip (per-row 'you are here' indicator at the top of
   the workflow actions block). Width-flexible: stages wrap on narrow sidebars. */
.lr-wf-strip-host {
  width: 100%;
}
.lr-wf-strip {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
  padding: 6px 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}
.lr-wf-strip-stages {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
}
.lr-wf-strip-stage {
  padding: 2px 8px;
  border-radius: 3px;
  white-space: nowrap;
}
.lr-wf-strip-stage.is-active {
  background: #2563eb;
  color: white;
}
.lr-wf-strip-stage.is-muted {
  background: #e5e7eb;
  color: #9ca3af;
}
.lr-wf-strip-sep {
  color: #d1d5db;
  font-size: 12px;
  user-select: none;
}
.lr-wf-strip-caption {
  font-size: 11px;
  color: #4b5563;
  font-weight: 500;
}

/* Snapshot viewer sandbox wrapper */
#__lr_snapshot_root {
  isolation: isolate;
  position: relative;
  width: 100%;
  min-height: 100vh;
}

/* ── Overlay ── */
.lr-overlay {
  position: fixed;
  pointer-events: none;
  border: 2px solid #2d7f7f;
  border-radius: 3px;
  background: rgba(45, 127, 127, 0.06);
  transition: all 0.1s ease;
  z-index: 2147483646;
  display: none;
}

.lr-overlay.active {
  display: block;
}

/* ── Sidebar ── */
.lr-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 360px;
  min-width: 280px;
  max-width: 600px;
  bottom: 0;
  background: #fff;
  border-left: 1px solid #e0e0e0;
  z-index: 2147483647;
  overflow-y: auto;
  transform: translateX(100%);
  transition: transform 0.2s ease;
}

.lr-sidebar.open {
  transform: translateX(0);
}

/* Resize handle on left edge */
.lr-sidebar-resize {
  position: absolute;
  top: 0;
  left: -4px;
  width: 8px;
  bottom: 0;
  cursor: col-resize;
  z-index: 1;
}

.lr-sidebar-resize:hover,
.lr-sidebar-resize.active {
  background: rgba(45, 127, 127, 0.3);
}

.lr-sidebar-header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.lr-sidebar-title {
  font-weight: 600;
  font-size: 15px;
}

.lr-sidebar-close {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px 8px;
  line-height: 1;
}

.lr-sidebar-close:hover {
  color: #1a1a2e;
}

.lr-sidebar-body {
  padding: 16px;
}

.lr-field {
  margin-bottom: 16px;
}

.lr-field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.lr-field-key {
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 12px;
  color: #2d7f7f;
  background: #f0f7f7;
  padding: 4px 8px;
  border-radius: 4px;
  word-break: break-all;
}

.lr-field-original {
  font-size: 13px;
  color: #333;
  background: #f8f8f8;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #eee;
  white-space: pre-wrap;
  word-break: break-word;
}

.lr-field-tag {
  display: inline;
  background: #d9eded;
  color: #2d7f7f;
  padding: 0 3px;
  border-radius: 2px;
  font-family: monospace;
  font-size: 12px;
}

.lr-textarea {
  width: 100%;
  min-height: 100px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
}

.lr-textarea:focus {
  border-color: #2d7f7f;
  box-shadow: 0 0 0 3px rgba(45, 127, 127, 0.1);
}

.lr-preview {
  padding: 10px 12px;
  background: #f8f8f8;
  border: 1px solid #eee;
  border-radius: 6px;
  font-size: 13px;
  min-height: 40px;
}

.lr-status {
  padding: 10px 16px;
  font-size: 12px;
  color: #666;
  text-align: center;
  border-top: 1px solid #eee;
  background: #fafafa;
}

.lr-status.saved {
  color: #22c55e;
}

.lr-status.error {
  color: #ef4444;
}

.lr-status.unsaved {
  color: #f59e0b;
}

/* ── Page blocks list ── */
.lr-blocks-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.lr-blocks-title {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.lr-block-item {
  padding: 8px 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  margin-bottom: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  overflow: hidden;
  transition: background 0.1s;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.lr-block-item:hover {
  background: #f0f7f7;
  border-color: #2d7f7f;
}

.lr-block-item.active {
  background: #e5f2f2;
  border-color: #2d7f7f;
}

/* ── Status dots ── */
.lr-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
  background: #d1d5db;
}

.lr-status-dot.missing { background: #d1d5db; }
.lr-status-dot.mt { background: #60a5fa; }
.lr-status-dot.edited { background: #f97316; }
.lr-status-dot.confirmed { background: #22c55e; }
.lr-status-dot.ready { background: #22c55e; }
.lr-status-dot.locked { background: #a855f7; }
.lr-status-dot.unsaved { background: #f59e0b; }
.lr-status-dot.readonly { background: #9ca3af; opacity: 0.6; } /* grey + faded — past my stage */

/* ── Locked textarea ── */
.lr-textarea-locked {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
  border-color: #e5e7eb;
}

/* ── Block text layout ── */
.lr-block-text {
  overflow: hidden;
  min-width: 0;
}

.lr-block-source {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lr-block-preview {
  font-size: 11px;
  color: #888;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
}
`;
